import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { brandBibleService } from '../services/brandBibleService';
import { notifyBrandBibleWritten } from './useClientCapabilities';
import type { BrandBible } from '../types/tenant';
import { message } from 'antd';

/**
 * The ONE brand-bible query key, shared by the editor (this hook) and the other
 * Brand Bible readers. A save invalidates it and every consumer refetches in the
 * same tick, so they can never drift apart.
 */
export const brandBibleKeys = {
  detail: (website: string) => ['brand-bible', website] as const,
};

export const useBrandBible = (website: string | null) => {
  return useQuery({
    queryKey: brandBibleKeys.detail(website ?? ''),
    queryFn: async () => {
      if (!website) return null;
      return brandBibleService.get(website);
    },
    enabled: !!website, // Only run query if website is provided
  });
};

export const useCreateBrandBible = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (brandBible: BrandBible) =>
      brandBibleService.createOrUpdate(brandBible.website ?? '', brandBible),
    onSuccess: (_data, variables) => {
      message.success('Brand Bible created successfully');
      // Invalidate the ONE shared key (editor + theme repaint in the same tick).
      if (variables.website) {
        queryClient.invalidateQueries({ queryKey: brandBibleKeys.detail(variables.website) });
      }
      // The ONE capability model hears about the save too (optimistic +
      // invalidation of ['client-capabilities', website]).
      if (variables.website) notifyBrandBibleWritten(queryClient, variables.website);
    },
    onError: (error) => {
      message.error('Failed to create brand bible');
      console.error(error);
    },
  });
};

export const useUpdateBrandBible = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BrandBible }) =>
      brandBibleService.createOrUpdate(data.website ?? '', data),
    onSuccess: (_data, variables) => {
      message.success('Brand Bible updated successfully');
      if (variables.data.website) {
        queryClient.invalidateQueries({ queryKey: brandBibleKeys.detail(variables.data.website) });
      }
      // The ONE capability model hears about the save too (optimistic +
      // invalidation of ['client-capabilities', website]).
      if (variables.data.website) notifyBrandBibleWritten(queryClient, variables.data.website);
    },
    onError: (error) => {
      message.error('Failed to update brand bible');
      console.error(error);
    },
  });
};

/**
 * CORE-K-09: narrow write of `service_locations` from the LocationPicker
 * "Workspace default locations" modal. Same cache story as the full
 * update: invalidate the bible and tell the capability model — the strip
 * that opened the modal re-renders from the refetched bible. No antd
 * toast here; the modal shows its own inline result line.
 */
export const usePatchBrandBibleServiceLocations = (website: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (serviceLocations: string[]) =>
      brandBibleService.patchServiceLocations(website, serviceLocations),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brandBible', website] });
      notifyBrandBibleWritten(queryClient, website);
    },
  });
};
